import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { Container } from '@tencent/alloy-renderer';
import { getElementRect } from '@editor/src/page/index/components/canvas/element-wrapper/utils';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { getFlip } from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { isFlipH, isFlipV } from '@editor/src/page/index/components/canvas/common';

/**
 * 将图形进行逆向翻转，目的是抵消图形最外层wrapper的整体翻转
 * @param component 图形Component，确保本身的宽高与wrapper一致，且本身没有位移或者翻转
 * @param factoryOptions 统一入参
 * @param isText 是否为文本，文本的逆向比较特殊，详见dealFlip
 */
export function reverseComponentFlip<T extends Container>(
    component: T,
    factoryOptions: InterfaceFactoryOptions,
    isText = false,
): T {
    const reverseFlip = getReverseFlip(factoryOptions, isText);
    const rect = getElementRect({ element: factoryOptions.model, tableResizing: false, useAdvHas: false });
    const { width, height } = rect;
    // 将翻转中心点改为区域中心
    component.x = width / 2;
    component.y = height / 2;
    component.pivotX = width / 2;
    component.pivotY = height / 2;
    // 翻转
    component.scaleX = reverseFlip.scaleX;
    component.scaleY = reverseFlip.scaleY;
    return component;
}

function getReverseFlip(
    factoryOptions: InterfaceFactoryOptions,
    isText,
): { scaleX: number, scaleY: number } {
    let scaleX;
    let scaleY;
    const { model, isGroupChildren } = factoryOptions;
    const flip = dealFlip(model, isText);
    if (!isGroupChildren || model.parent?.type !== UIModelType.GROUP) {
        // 非组合
        scaleX = flip.scaleX;
        scaleY = flip.scaleY;
    } else {
        // 组合
        const groupFlip = getGroupFlip(model.parent, flip, isText);
        scaleX = groupFlip.scaleX;
        scaleY = groupFlip.scaleY;
    }
    return { scaleX, scaleY };
}

// 区分正常逆向与文本的逆向，得到需要逆向的翻转值
function dealFlip(model, isText) {
    const rect = getElementRect({ element: model, tableResizing: false, useAdvHas: false });
    const flip = getFlip(rect, model.props);
    const scaleX = isFlipH(flip) ? -1 : 1;
    const scaleY = isFlipV(flip) ? -1 : 1;
    if (isText) {
        // 与office统一（即横向不跟随主体翻转，纵向跟随主体翻转。注意：虽然纵向跟随，但是为了文本可读，一旦主体纵向发生翻转，需要让横向翻转一次）
        // 也就是纵向永远不做逆向，横向 = 本身的逆向 * 纵向触发的横向的逆向
        return { scaleX: scaleX * scaleY, scaleY: 1 };
    }
    // 通用的逆向，即横向纵向均再次翻转
    return { scaleX, scaleY };
}

// 组合的元素需要再根据各层组合的翻转做逆向
function getGroupFlip(parentModel, preFlip, isText) {
    const res = dealFlip(parentModel, isText);
    const selfGroupFlip = {
        scaleX: preFlip.scaleX * res.scaleX,
        scaleY: preFlip.scaleY * res.scaleY,
    };
    if (parentModel.parent?.type === UIModelType.GROUP) {
        return getGroupFlip(parentModel.parent, selfGroupFlip, isText);
    }
    return selfGroupFlip;
}
